import * as React from 'react';
import { PrintButton } from '../components/PrintPreview';
import {
  CDPage1,
  type CDPage1Data,
  CDPage2,
  type CDPage2Data,
  CDPage3,
  type CDPage3Data,
  CDPage4,
  type CDPage4Data,
  CDPage5,
  type CDPage5Data,
} from '../components/pages';

// Import print styles
import '../styles/print.css';

// Sample data for demonstration
const samplePage1Data = {
  header: {
    closingInformation: {
      dateIssued: '4/15/2013',
      closingDate: '4/15/2013',
      disbursementDate: '4/15/2013',
      settlementAgent: 'Epsilon Title Co.',
      fileNumber: '12-3456',
      propertyAddress: '456 Somewhere Ave\nAnytown, ST 12345',
      salePrice: 180000,
    },
    transactionInformation: {
      borrowerName: 'Michael Jones and Mary Stone\n123 Anywhere Street\nAnytown, ST 12345',
      sellerName: 'Steve Cole and Amy Doe\n321 Somewhere Drive\nAnytown, ST 12345',
      lenderName: 'Ficus Bank\n4321 Random Blvd.\nSomecity, ST 12340',
    },
    loanInformation: {
      loanTerm: '30 years',
      purpose: 'Purchase',
      product: 'Fixed Rate',
      loanType: 'Conventional' as const,
      loanIdNumber: '123456789',
      mic: '000654321',
    },
  },
  loanTerms: {
    loanAmount: {
      value: 162000,
      canIncrease: false,
    },
    interestRate: {
      value: 3.875,
      canIncrease: false,
    },
    monthlyPrincipalAndInterest: {
      value: 761.78,
      canIncrease: false,
    },
    prepaymentPenalty: {
      hasFeature: true,
      maxAmount: 3240,
    },
    balloonPayment: {
      hasFeature: false,
    },
  },
  projectedPayments: {
    periods: [
      {
        yearRange: 'Years 1-7',
        principalAndInterest: 761.78,
        mortgageInsurance: 82,
        estimatedEscrow: 206,
        estimatedTotalMonthlyPayment: 1049.78,
      },
      {
        yearRange: 'Years 8-30',
        principalAndInterest: 761.78,
        mortgageInsurance: 0,
        estimatedEscrow: 206,
        estimatedTotalMonthlyPayment: 967.78,
      },
    ],
    estimatedTaxesInsuranceAssessments: 206,
    escrowDescription: "Property Taxes, Homeowner's Insurance",
    inEscrow: ['Property Taxes', "Homeowner's Insurance"],
    notInEscrow: ["Homeowner's Association Dues"],
  },
  costsAtClosing: {
    closingCosts: 9712.1,
    loanCosts: 4664.25,
    otherCosts: 5047.85,
    lenderCredits: 0,
    cashToClose: 14147.26,
    fromTo: 'From Borrower',
  },
};

const samplePage2Data = {
  loanCosts: {
    originationCharges: {
      items: [
        {
          lineNumber: '01',
          description: '0.25% of Loan Amount (Points)',
          borrowerPaid: { atClosing: 405 },
        },
        { lineNumber: '02', description: 'Application Fee', borrowerPaid: { atClosing: 300 } },
        { lineNumber: '03', description: 'Underwriting Fee', borrowerPaid: { atClosing: 1097 } },
      ],
      subtotal: 1802,
    },
    servicesYouCannotShopFor: {
      items: [
        {
          lineNumber: '04',
          description: 'Appraisal Fee',
          toEntity: 'to ABC Appraisal Co.',
          borrowerPaid: { atClosing: 405 },
        },
        {
          lineNumber: '05',
          description: 'Credit Report Fee',
          toEntity: 'to XYZ Credit Bureau',
          borrowerPaid: { atClosing: 29.8 },
        },
        {
          lineNumber: '06',
          description: 'Flood Determination Fee',
          toEntity: 'to Flood Data Inc.',
          borrowerPaid: { atClosing: 20 },
        },
        {
          lineNumber: '07',
          description: 'Flood Monitoring Fee',
          toEntity: 'to Flood Data Inc.',
          borrowerPaid: { atClosing: 31.75 },
        },
        {
          lineNumber: '08',
          description: 'Tax Monitoring Fee',
          toEntity: 'to Tax Services Inc.',
          borrowerPaid: { atClosing: 75 },
        },
        {
          lineNumber: '09',
          description: 'Tax Status Research Fee',
          toEntity: 'to Tax Services Inc.',
          borrowerPaid: { atClosing: 80 },
        },
      ],
      subtotal: 641.55,
    },
    servicesYouCanShopFor: {
      items: [
        {
          lineNumber: '10',
          description: 'Pest Inspection Fee',
          toEntity: 'to Pest Busters Inc.',
          borrowerPaid: { atClosing: 120.5 },
        },
        {
          lineNumber: '11',
          description: 'Survey Fee',
          toEntity: 'to Surveys R Us',
          borrowerPaid: { atClosing: 85 },
        },
        {
          lineNumber: '12',
          description: 'Title - Insurance Binder',
          toEntity: 'to Epsilon Title Co.',
          borrowerPaid: { atClosing: 700 },
        },
        {
          lineNumber: '13',
          description: "Title - Lender's Title Policy",
          toEntity: 'to Epsilon Title Co.',
          borrowerPaid: { atClosing: 535 },
        },
        {
          lineNumber: '14',
          description: 'Title - Settlement Agent Fee',
          toEntity: 'to Epsilon Title Co.',
          borrowerPaid: { atClosing: 502 },
        },
        {
          lineNumber: '15',
          description: 'Title - Title Search',
          toEntity: 'to Epsilon Title Co.',
          borrowerPaid: { atClosing: 278.2 },
        },
      ],
      subtotal: 2220.7,
    },
    totalLoanCosts: 4664.25,
  },
  otherCosts: {
    taxesAndGovernmentFees: {
      items: [
        { lineNumber: 'E.01', description: 'Recording Fees', borrowerPaid: { atClosing: 85 } },
        { lineNumber: 'E.02', description: 'Transfer Taxes', sellerPaid: { atClosing: 950 } },
      ],
      subtotal: 85,
    },
    prepaids: {
      items: [
        {
          lineNumber: 'F.01',
          description: "Homeowner's Insurance Premium (12 mo.)",
          borrowerPaid: { atClosing: 1209.96 },
        },
        {
          lineNumber: 'F.02',
          description: 'Mortgage Insurance Premium (1 mo.)',
          borrowerPaid: { atClosing: 82 },
        },
        {
          lineNumber: 'F.03',
          description: 'Prepaid Interest ($17.44 per day for 15 days)',
          borrowerPaid: { atClosing: 261.6 },
        },
        {
          lineNumber: 'F.04',
          description: 'Property Taxes (6 mo.)',
          borrowerPaid: { atClosing: 631.8 },
        },
      ],
      subtotal: 2185.36,
    },
    initialEscrowPaymentAtClosing: {
      items: [
        {
          lineNumber: 'G.01',
          description: "Homeowner's Insurance ($100.83 per mo. for 2 mo.)",
          borrowerPaid: { atClosing: 201.66 },
        },
        {
          lineNumber: 'G.02',
          description: 'Mortgage Insurance ($82.00 per mo. for 2 mo.)',
          borrowerPaid: { atClosing: 164 },
        },
        {
          lineNumber: 'G.03',
          description: 'Property Taxes ($105.30 per mo. for 2 mo.)',
          borrowerPaid: { atClosing: 210.6 },
        },
      ],
      subtotal: 576.26,
    },
    other: {
      items: [
        {
          lineNumber: 'H.01',
          description: "Title - Owner's Title Insurance (optional)",
          borrowerPaid: { atClosing: 1000 },
        },
      ],
      subtotal: 1000,
    },
    totalOtherCosts: 5047.85,
  },
  totalClosingCosts: {
    closingCostsSubtotal: 9712.1,
    lenderCredits: 0,
  },
};

const samplePage3Data = {
  calculatingCashToClose: {
    totalClosingCosts: {
      label: 'Total Closing Costs (J)',
      loanEstimate: 8054.0,
      final: 9712.1,
      didChange: true,
    },
    closingCostsPaidBeforeClosing: {
      label: 'Closing Costs Paid Before Closing',
      loanEstimate: 0,
      final: -29.8,
      didChange: true,
    },
    closingCostsFinanced: {
      label: 'Closing Costs Financed (Paid from your Loan Amount)',
      loanEstimate: 0,
      final: 0,
    },
    downPaymentFromBorrower: {
      label: 'Down Payment/Funds from Borrower',
      loanEstimate: 18000.0,
      final: 18000.0,
    },
    deposit: { label: 'Deposit', loanEstimate: -10000.0, final: -10000.0 },
    fundsForBorrower: { label: 'Funds for Borrower', loanEstimate: 0, final: 0 },
    sellerCredits: { label: 'Seller Credits', loanEstimate: -2500.0, final: -2500.0 },
    adjustmentsAndOtherCredits: {
      label: 'Adjustments and Other Credits',
      loanEstimate: -1035.04,
      final: -1035.04,
    },
    cashToClose: {
      label: 'Cash to Close',
      loanEstimate: 12518.96,
      final: 14147.26,
      didChange: true,
    },
  },
  summariesOfTransactions: {
    borrower: {
      dueToBorrower: {
        title: 'Due from Borrower at Closing',
        items: [
          { lineNumber: '01', description: 'Sale Price of Property', amount: 180000 },
          {
            lineNumber: '02',
            description: 'Sale Price of Any Personal Property Included in Sale',
            amount: 0,
          },
          { lineNumber: '03', description: 'Closing Costs Paid at Closing (J)', amount: 9682.3 },
        ],
        adjustments: [
          { lineNumber: '04', description: 'City/Town Taxes to 4/15/13', amount: 0 },
          { lineNumber: '05', description: 'County Taxes to 4/15/13', amount: 365.04 },
          { lineNumber: '06', description: 'Assessments to', amount: 0 },
        ],
        subtotal: 190047.34,
      },
      paidAlreadyByBorrower: {
        title: 'Paid Already by or on Behalf of Borrower at Closing',
        items: [
          { lineNumber: '01', description: 'Deposit', amount: 10000 },
          { lineNumber: '02', description: 'Loan Amount', amount: 162000 },
          {
            lineNumber: '03',
            description: 'Existing Loan(s) Assumed or Taken Subject to',
            amount: 0,
          },
          { lineNumber: '04', description: 'Seller Credit', amount: 2500 },
          { lineNumber: '05', description: 'Other Credits', amount: 1400.08 },
        ],
        subtotal: 175900.08,
      },
      cashToClose: 14147.26,
      cashDirection: 'from' as const,
    },
    seller: {
      dueToSeller: {
        title: 'Due to Seller at Closing',
        items: [
          { lineNumber: '01', description: 'Sale Price of Property', amount: 180000 },
          {
            lineNumber: '02',
            description: 'Sale Price of Any Personal Property Included in Sale',
            amount: 0,
          },
        ],
        adjustments: [
          { lineNumber: '03', description: 'City/Town Taxes to 4/15/13', amount: 0 },
          { lineNumber: '04', description: 'County Taxes to 4/15/13', amount: 365.04 },
          { lineNumber: '05', description: 'Assessments to', amount: 0 },
        ],
        subtotal: 180365.04,
      },
      dueFromSeller: {
        title: 'Due from Seller at Closing',
        items: [
          { lineNumber: '01', description: 'Excess Deposit', amount: 0 },
          { lineNumber: '02', description: 'Closing Costs Paid at Closing (J)', amount: 950 },
          {
            lineNumber: '03',
            description: 'Existing Loan(s) Assumed or Taken Subject to',
            amount: 0,
          },
          { lineNumber: '04', description: 'Payoff of First Mortgage Loan', amount: 100000 },
          { lineNumber: '05', description: 'Payoff of Second Mortgage Loan', amount: 0 },
          { lineNumber: '06', description: 'Seller Credit', amount: 2500 },
        ],
        subtotal: 103450,
      },
      cashFromSeller: 76915.04,
      cashDirection: 'to' as const,
    },
  },
};

const samplePage4Data = {
  loanDisclosures: {
    assumption: {
      allowed: false,
    },
    demandFeature: {
      hasDemandFeature: false,
    },
    latePayment: {
      daysAfterDue: 15,
      feeAmount: 5,
      feeIsPercentage: true,
    },
    negativeAmortization: {
      hasNegativeAmortization: false,
    },
    partialPayments: {
      mayAccept: false,
      mayHoldInAccount: false,
      mayApplyToLoan: false,
      mayReturnToBorrower: true,
    },
    securityInterest: {
      propertyAddress: '456 Somewhere Ave., Anytown, ST 12345',
    },
    escrowAccount: {
      hasEscrow: true,
    },
  },
  escrowAccount: {
    hasEscrow: true,
    monthlyEscrowPayment: 206.13,
    items: [
      { description: "Homeowner's Insurance", monthlyAmount: 100.83 },
      { description: 'Property Taxes', monthlyAmount: 105.3 },
    ],
    initialEscrowPaymentAtClosing: 576.26,
    nonEscrowPropertyCosts: 1800,
  },
};

const samplePage5Data = {
  loanCalculations: {
    totalOfPayments: 285803.36,
    financeCharge: 118830.27,
    amountFinanced: 162000,
    annualPercentageRate: 4.174,
    totalInterestPercentage: 69.46,
  },
  otherDisclosures: {
    appraisal: {
      rightToCopy: true,
    },
    contract: {},
    liabilityAfterForeclosure: {
      hasDeficiencyRisk: true,
      stateAllowsDeficiency: true,
    },
    refinance: {
      refinancingSameProperty: false,
    },
    taxDeductions: {},
  },
  contactInformation: {
    lender: {
      name: 'Ficus Bank',
      address: '4321 Random Blvd.\nSomecity, ST 12340',
      nmls: '222222',
      stateLicense: '',
      contactName: 'Joe Smith',
      contactNmls: '12345',
      contactStateLicense: '',
      email: 'joesmith@ficusbank.com',
      phone: '123-456-7890',
    },
    settlementAgent: {
      name: 'Epsilon Title Co.',
      address: '123 ABC Street\nSomewhere, ST 12340',
      nmls: '',
      stateLicense: 'Z61456',
      contactName: 'Sarah Arnold',
      contactNmls: '',
      contactStateLicense: 'PT1234',
      email: 'sarah@epsilontitle.com',
      phone: '987-654-3210',
    },
    realEstateBrokerBorrower: {
      name: 'Omega Real Estate Broker Inc.',
      address: '789 Local Lane\nHometown, ST 12340',
      nmls: '',
      stateLicense: 'Z61616',
      contactName: 'Samuel Green',
      contactNmls: '',
      contactStateLicense: 'PT5678',
      email: 'sam@omegare.biz',
      phone: '222-333-4444',
    },
    realEstateBrokerSeller: {
      name: 'Alpha Real Estate Broker Co.',
      address: '987 Suburb Ct.\nHometown, ST 12340',
      nmls: '',
      stateLicense: 'Z61617',
      contactName: 'Joseph Cain',
      contactNmls: '',
      contactStateLicense: 'PT9876',
      email: 'joe@alphare.biz',
      phone: '321-555-7171',
    },
  },
  confirmReceipt: {
    applicants: [
      { name: 'Michael Jones', signature: '', date: '' },
      { name: 'Mary Stone', signature: '', date: '' },
    ],
  },
};

const CDPreview: React.FC = () => {
  const [isEditable, setIsEditable] = React.useState(false);
  const [page1Data, setPage1Data] = React.useState(samplePage1Data);
  const [page2Data, setPage2Data] = React.useState(samplePage2Data);
  const [page3Data, setPage3Data] = React.useState(samplePage3Data);
  const [page4Data, setPage4Data] = React.useState(samplePage4Data);
  const [page5Data, setPage5Data] = React.useState(samplePage5Data);

  // Wrapper functions that merge partial updates with existing state
  const handlePage1Change = React.useCallback((data: Partial<CDPage1Data>) => {
    setPage1Data((prev) => ({ ...prev, ...data }) as typeof samplePage1Data);
  }, []);

  const handlePage2Change = React.useCallback((data: Partial<CDPage2Data>) => {
    setPage2Data((prev) => ({ ...prev, ...data }) as typeof samplePage2Data);
  }, []);

  const handlePage3Change = React.useCallback((data: Partial<CDPage3Data>) => {
    setPage3Data((prev) => ({ ...prev, ...data }) as typeof samplePage3Data);
  }, []);

  const handlePage4Change = React.useCallback((data: Partial<CDPage4Data>) => {
    setPage4Data((prev) => ({ ...prev, ...data }) as typeof samplePage4Data);
  }, []);

  const handlePage5Change = React.useCallback((data: Partial<CDPage5Data>) => {
    setPage5Data((prev) => ({ ...prev, ...data }) as typeof samplePage5Data);
  }, []);

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-[900px] mx-auto">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Closing Disclosure Preview</h1>
          <p className="text-gray-600">CFPB Standard 5-Page Closing Disclosure Form</p>
          <div className="mt-4 flex gap-3 justify-center cd-no-print">
            <button
              type="button"
              onClick={() => setIsEditable(!isEditable)}
              className={`px-4 py-2 rounded-md font-medium transition-colors ${
                isEditable
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {isEditable ? 'Edit Mode: ON' : 'Edit Mode: OFF'}
            </button>
            <PrintButton documentTitle="Closing Disclosure" />
          </div>
        </div>

        {/* Page 1 */}
        <div className="mb-8 shadow-lg">
          <CDPage1
            data={page1Data}
            editable={isEditable}
            onChange={handlePage1Change}
            formId="CFPB Form H-25(A)"
          />
        </div>

        {/* Page 2 */}
        <div className="mb-8 shadow-lg">
          <CDPage2
            data={page2Data}
            editable={isEditable}
            onChange={handlePage2Change}
            formId="CFPB Form H-25(A)"
          />
        </div>

        {/* Page 3 */}
        <div className="mb-8 shadow-lg">
          <CDPage3
            data={page3Data}
            editable={isEditable}
            onChange={handlePage3Change}
            formId="CFPB Form H-25(A)"
          />
        </div>

        {/* Page 4 */}
        <div className="mb-8 shadow-lg">
          <CDPage4
            data={page4Data}
            editable={isEditable}
            onChange={handlePage4Change}
            formId="CFPB Form H-25(A)"
          />
        </div>

        {/* Page 5 */}
        <div className="mb-8 shadow-lg">
          <CDPage5
            data={page5Data}
            editable={isEditable}
            onChange={handlePage5Change}
            formId="CFPB Form H-25(A)"
          />
        </div>
      </div>
    </div>
  );
};

export default CDPreview;
